import React, { useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Swal from "sweetalert2";

const PROSAssessmentList = ({
  assessments = [],
  onAddAssessment,
  onEditAssessment,
  onDeleteAssessment,
}) => {
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // === Filter Logic ===
  const filteredRecords = useMemo(() => {
    return assessments.filter((r) =>
      Object.values(r).some((v) =>
        String(v || "").toLowerCase().includes(filterText.toLowerCase())
      )
    );
  }, [assessments, filterText]);

  const totalRows = filteredRecords.length;
  const start = (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  // === Custom Pagination ===
  const CustomPagination = ({
    onChangePage,
    onChangeRowsPerPage,
    currentPage,
    rowsPerPage,
    rowCount,
  }) => {
    const totalPages = Math.ceil(rowCount / rowsPerPage);
    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">Rows per page:</span>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px" }}
            value={rowsPerPage}
            onChange={(e) =>
              onChangeRowsPerPage(Number(e.target.value), currentPage)
            }
          >
            {[5, 10, 15, 20, 25].map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex align-items-center gap-1">
          <button
            className="btn btn-sm border"
            onClick={() => onChangePage(currentPage - 1, rowCount)}
            disabled={currentPage === 1}
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>

          <span className="mx-3 text-muted small">
            Page {currentPage} of {totalPages}
          </span>

          <button
            className="btn btn-sm border"
            onClick={() => onChangePage(currentPage + 1, rowCount)}
            disabled={currentPage === totalPages}
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  // === Export PDF ===
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("PROS Assessments", 14, 15);
    const tableColumn = [
      "#",
      "Client Name",
      "Effective Date",
      "Practitioner",
      "Created By",
      "Updated By",
    ];
    const tableRows = filteredRecords.map((r, i) => [
      i + 1,
      r.client_name || "-",
      r.effective_date || "-",
      r.part3?.practitioner_recommendations?.substring(0, 30) + "..." || "-",
      r.creator?.name || "-",
      r.updater?.name || "-",
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 25,
      theme: "striped",
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save("pros_assessments.pdf");
  };

  // === Columns ===
  const columns = [
    {
      name: "S.No",
      cell: (_, index) => index + 1 + (currentPage - 1) * rowsPerPage,
      width: "70px",
    },
    {
      name: "Client",
      selector: (row) => row.client_name || "-",
      sortable: true,
    },
    {
      name: "Effective Date",
      selector: (row) => row.effective_date || "-",
      sortable: true,
    },
    {
      name: "Practitioner",
      cell: (row) =>
        row.part3?.practitioner_recommendations
          ? row.part3.practitioner_recommendations.substring(0, 30) + "..."
          : "-",
      sortable: true,
    },
    {
      name: "Created By",
      selector: (row) => row.creator?.name || "-",
      sortable: true,
    },
    {
      name: "Updated By",
      selector: (row) => row.updater?.name || "-",
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-1">
          {onEditAssessment && (
            <button
              className="btn btn-sm btn-outline-primary"
              title="Edit"
              onClick={() => onEditAssessment(row.id)}
            >
              <i className="ri-edit-line"></i>
            </button>
          )}
          {onDeleteAssessment && (
            <button
              className="btn btn-sm btn-outline-danger"
              title="Delete"
              onClick={() => onDeleteAssessment(row.id)}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          )}
        </div>
      ),
      width: "110px",
      ignoreRowClick: true,
      button: true,
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2 p-3">
        <div className="d-flex align-items-center gap-2">
          <div className="dropdown">
            <button
              className="btn btn-label-primary btn-sm dropdown-toggle"
              data-bs-toggle="dropdown"
            >
              <i className="ri-download-2-line me-1"></i> Export
            </button>
            <ul className="dropdown-menu shadow-sm">
              <li>
                <button className="dropdown-item" onClick={handleExportPDF}>
                  <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                </button>
              </li>
              <li>
                <CSVLink
                  data={filteredRecords}
                  filename="pros_assessments.csv"
                  className="dropdown-item"
                >
                  <i className="ri-file-excel-line me-1 text-success"></i> CSV
                </CSVLink>
              </li>
            </ul>
          </div>

          <input
            type="text"
            placeholder="Search assessments..."
            className="form-control form-control-sm"
            style={{ width: "220px" }}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        <h6 className="mb-0 text-center text-muted flex-grow-1 small">
          Showing {start}-{end} of {totalRows} records
        </h6>

        <div>
          {onAddAssessment && (
            <button
              className="btn btn-sm btn-primary"
              onClick={onAddAssessment}
            >
              <i className="ri-add-line me-1"></i> Add New
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card-body p-0">
        <DataTable
          columns={columns}
          data={filteredRecords}
          pagination
          paginationPerPage={rowsPerPage}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={setRowsPerPage}
          onChangePage={setCurrentPage}
          paginationComponent={CustomPagination}
          highlightOnHover
          striped
          responsive
          noDataComponent={
            <div className="p-4 text-center text-muted">
              No PROS Assessments found
            </div>
          }
        />
      </div>
    </div>
  );
};

export default PROSAssessmentList;